import { useFetchCompletedForm } from '@/api/hooks/useFetchCompletedForm'
import type { CompletedForm, PendingForm } from '@/api/types/Forms'
import { ErrorState } from '@/components/ErrorState'
import { SubmittedAnswers } from '@/components/FormsPage/SubmittedAnswers'
import { formatSubmittedOn } from '@/components/FormsPage/formatting'
import { openForm } from '@/components/HomePage/HomeTiles/TodoFormRow'
import { Dot, Row, RowGroup, SeeAllRow } from '@/components/Rows'
import { SheetStack, type StackView } from '@/components/Rows/SheetStack'
import { Button, ButtonText } from '@/ui/core/button'
import { SheetTitle } from '@/ui/core/sheet'
import { Skeleton } from '@/ui/core/skeleton'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { useState } from 'react'
import { View } from 'react-native'

const LIVE_MAX = 3

/**
 * A row's state lives under its name: a hollow ring for a form still waiting, the same circle
 * ticked once it is in. `before` closes the sheet first — pushing a route under an open modal
 * navigates behind it, so the tap looks like nothing happened.
 */
const PendingRows = ({
	forms,
	last,
	before,
}: { forms: PendingForm[]; last: boolean; before?: () => void }) => (
	<>
		{forms.map((form, i) => (
			<Row
				key={`p${form.id}`}
				status="due"
				title={form.name}
				tag={<Dot />}
				onPress={() => {
					before?.()
					openForm(form)
				}}
				last={last && i === forms.length - 1}
			/>
		))}
	</>
)

const CompletedRows = ({
	forms,
	last,
	onOpen,
}: {
	forms: CompletedForm[]
	last: boolean
	onOpen: (form: CompletedForm) => void
}) => (
	<>
		{forms.map((form, i) => (
			<Row
				key={`c${form.id}`}
				status="done"
				title={form.name}
				line={`Submitted ${formatSubmittedOn(form.submitted)}`}
				onPress={() => onOpen(form)}
				last={last && i === forms.length - 1}
			/>
		))}
	</>
)

/**
 * A submitted form, inside the sheet that listed it: the pill saying it is in, its name, and
 * every answer. The record is fetched on the way in, so the sheet steps across while it loads
 * rather than waiting on the tap.
 */
const CompletedPanel = ({
	form,
	onBack,
}: { form: CompletedForm; onBack?: () => void }) => {
	const { completedForm, isLoading, isError, refetch } = useFetchCompletedForm(form.id)

	return (
		<View className="pt-2 pb-2">
			<SheetTitle
				glyph="form"
				onBack={onBack}
				line={`Submitted ${formatSubmittedOn(form.submitted)}`}
			>
				{form.name}
			</SheetTitle>
			<VStack gap={20}>
				<VStack gap={20} className="px-4">
					<Skeleton isLoading={isLoading} className="h-24 w-full rounded-note bg-blush">
						{isError || !completedForm ? (
							// It stated the problem and offered nothing to do about it, which is the
							// worst of the seven this component replaced.
							<ErrorState
								fit="sheet"
								title="We couldn't read this form"
								line="Check your connection and try again."
								onAction={() => refetch()}
							/>
						) : (
							<SubmittedAnswers entries={completedForm.entries} on="sheet" />
						)}
					</Skeleton>
				</VStack>
			</VStack>
		</View>
	)
}

/**
 * Forms, to do and done, in one card: every open form up to three, or the last one submitted
 * when nothing is open, and See All for the rest, to do first, then everything submitted.
 */
export const Forms = ({
	pending,
	completed,
	isLoading,
	hasMore,
	isFetchingMore,
	onMore,
}: {
	pending?: PendingForm[]
	completed: CompletedForm[]
	isLoading: boolean
	hasMore: boolean
	isFetchingMore: boolean
	onMore: () => void
}) => {
	const [view, setView] = useState<StackView<CompletedForm> | null>(null)
	const close = () => setView(null)
	// From the card the sheet opens straight on the record; from the list it steps across.
	const openFromCard = (form: CompletedForm) =>
		setView({ kind: 'item', item: form, fromList: false })
	const openFromList = (form: CompletedForm) =>
		setView({ kind: 'item', item: form, fromList: true })
	if (isLoading)
		return <Skeleton isLoading className="h-24 w-full rounded-note bg-blush" />
	const open = pending ?? []
	if (open.length === 0 && completed.length === 0) return null
	const shownPending = open.slice(0, LIVE_MAX)
	const shownCompleted = open.length ? [] : completed.slice(0, 1)
	const total = open.length + completed.length
	const shown = shownPending.length + shownCompleted.length
	const more = total > shown || hasMore
	return (
		<VStack gap={12}>
			<RowGroup title="Forms">
				<PendingRows forms={shownPending} last={!more && shownCompleted.length === 0} />
				<CompletedRows forms={shownCompleted} last={!more} onOpen={openFromCard} />
				{more && <SeeAllRow onPress={() => setView({ kind: 'list' })} />}
			</RowGroup>
			<SheetStack
				view={view}
				onClose={close}
				list={
					<View className="pt-2 pb-2">
						<SheetTitle glyph="form">Forms</SheetTitle>
						<VStack gap={16}>
							{open.length > 0 && (
								<RowGroup title="To do">
									<PendingRows forms={open} last before={close} />
								</RowGroup>
							)}
							{completed.length > 0 && (
								<RowGroup title="Completed">
									<CompletedRows forms={completed} last onOpen={openFromList} />
								</RowGroup>
							)}
							{hasMore && (
								<Button
									variant="secondary"
									size="sm"
									fullWidth={false}
									onPress={onMore}
									isLoading={isFetchingMore}
									disabled={isFetchingMore}
								>
									<ButtonText>Load more</ButtonText>
								</Button>
							)}
							{open.length === 0 && completed.length === 0 && (
								<Text tone="soft">Forms you submit will appear here.</Text>
							)}
						</VStack>
					</View>
				}
				item={(form) => (
					<CompletedPanel
						form={form}
						onBack={
							view?.kind === 'item' && view.fromList
								? () => setView({ kind: 'list' })
								: undefined
						}
					/>
				)}
			/>
		</VStack>
	)
}
